/**
 * useTextFilter — 텍스트 검색어 → 필터된 항목 또는 매칭된 ID 집합.
 *
 * dashboard 곳곳의 `query.toLowerCase() + 다중 필드 includes` 패턴 SSOT.
 * 빈 query 일 때는 별도 처리 (필터 없음 신호) 가 가능하도록 두 형태를 제공한다.
 *
 * 두 시나리오를 분리해서 제공한다:
 *  - `useFilteredItems` : items 를 그대로 필터링한다.
 *  - `useMatchedIds` : Set<id> 를 반환한다 (MasterGraphView 의 흐림 처리 등 — 미매칭 항목도
 *    렌더되어야 하는 경우).
 *  - `matchesQuery` : useMemo 외부 (예: 카테고리별 inner filter) 에서 직접 사용할 수 있는
 *    순수 함수.
 *
 * 두 훅 모두 query 가 비었거나 pickFields 가 빈 배열이면 빠른 경로를 탄다.
 *
 * @example
 *   const filtered = useFilteredItems(blocks, query, (b) => [b.displayName, b.id, b.description])
 *
 * @example
 *   const matchedIds = useMatchedIds(nodes, search, (n) => [n.name, n.kind, n.category ?? ""], (n) => n.id)
 *
 * @example
 *   // useMemo 안에서 카테고리별 필터에 활용
 *   const items = matchesQuery(query, [b.displayName, b.id, b.description]) ? filtered : all
 */
import { useMemo } from "react"

/**
 * matchesQuery — query 가 fields 중 하나에 대소문자 무시 부분 일치하는지 검사.
 *
 * - query 가 trim 후 빈 문자열이면 항상 true (필터 통과로 간주).
 * - fields 의 각 요소를 lowercase + includes 로 검사하다 첫 매치에서 true.
 *
 * useMemo 의 의존 배열을 단순화하기 위해 순수 함수로 노출.
 */
export function matchesQuery(query: string, fields: ReadonlyArray<string>): boolean {
	const q = query.trim().toLowerCase()
	if (!q) return true
	for (const f of fields) {
		if (f.toLowerCase().includes(q)) return true
	}
	return false
}

/**
 * 항목 → 검색 대상 문자열 배열.
 * 각 항목당 여러 필드를 검사할 수 있도록 string[] 반환.
 * `undefined` 필드는 호출자가 빈 문자열 ("") 로 대체한다.
 */
export type PickFieldsFn<T> = (item: T) => ReadonlyArray<string>

/**
 * 항목 → ID 추출. matchedIds 변형에서 Set<string> 키로 사용.
 */
export type PickIdFn<T> = (item: T) => string

/**
 * useFilteredItems — query 와 매칭되는 항목만 골라 새 배열로 반환.
 *
 * - query 가 trim 후 빈 문자열이면 items 를 그대로 (참조 동일하게) 반환.
 * - 비교는 항상 `toLowerCase + includes` (대소문자 무시 + 부분 일치).
 *
 * @param items 필터 대상 readonly 배열
 * @param query 검색어 (trim 안 된 원본 그대로 받아 내부에서 trim)
 * @param pickFields 항목에서 검사할 string 필드들을 반환하는 함수
 */
export function useFilteredItems<T>(
	items: ReadonlyArray<T>,
	query: string,
	pickFields: PickFieldsFn<T>
): ReadonlyArray<T> {
	return useMemo(() => {
		const q = query.trim().toLowerCase()
		if (!q) return items
		return items.filter((item) => {
			const fields = pickFields(item)
			for (const f of fields) {
				if (f.toLowerCase().includes(q)) return true
			}
			return false
		})
	}, [items, query, pickFields])
}

/**
 * useMatchedIds — query 와 매칭되는 항목의 ID 집합을 반환.
 *
 * - query 가 trim 후 빈 문자열이면 `undefined` 를 반환 (필터 없음 신호).
 *   호출자는 `undefined` 일 때 모든 항목을 표시하도록 분기한다.
 * - 매칭 항목의 `pickId(item)` 결과를 Set 에 담아 반환.
 *
 * @param items 검사 대상 readonly 배열
 * @param query 검색어 (trim 안 된 원본)
 * @param pickFields 항목에서 검사할 string 필드들
 * @param pickId 항목의 고유 ID 추출 함수
 * @returns 매칭된 ID Set, 또는 query 가 비었으면 `undefined`
 */
export function useMatchedIds<T>(
	items: ReadonlyArray<T>,
	query: string,
	pickFields: PickFieldsFn<T>,
	pickId: PickIdFn<T>
): ReadonlySet<string> | undefined {
	return useMemo(() => {
		const q = query.trim().toLowerCase()
		if (!q) return undefined
		const matched = new Set<string>()
		for (const item of items) {
			const fields = pickFields(item)
			for (const f of fields) {
				if (f.toLowerCase().includes(q)) {
					matched.add(pickId(item))
					break
				}
			}
		}
		return matched
	}, [items, query, pickFields, pickId])
}
